'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect } from 'react';
import { useSyncRef } from './use-sync-ref';
export interface AttentionIntervalOptions {
  /**
   * When true, fire the callback immediately on mount (if attentive) and on
   * each not-attentive→attentive transition, in addition to regular interval ticks.
   */
  immediate?: boolean;
  /** Idle timeout in ms. Default: 300_000 (5 minutes). */
  idleTimeoutMs?: number;
  /** Called once when attention is lost (blur, idle, or hidden). Best-effort, timer-driven for idle. */
  onLostAttention?: () => void;
}
const DEFAULT_IDLE_TIMEOUT_MS = 300_000;
const IDLE_CHECK_INTERVAL_MS = 10_000;
const ACTIVITY_THROTTLE_MS = 1000;

/**
 * Runs a callback on a repeating interval, but only when the user is actively
 * attending to the page: window focused, tab visible, and recent user activity.
 *
 * Fires `onLostAttention` once per attentive→inattentive transition. Focus and
 * visibility are event-driven; idle detection is timer-driven (internal timer
 * reset on activity events).
 */
export function useAttentionInterval(onTick, intervalMs, enabled, options) {
  const $ = _c(12);
  const onTickRef = useSyncRef(onTick);
  const onLostAttentionRef = useSyncRef(options?.onLostAttention);
  const immediate = options?.immediate ?? false;
  const idleTimeoutMs = options?.idleTimeoutMs ?? DEFAULT_IDLE_TIMEOUT_MS;
  let t0;
  if ($[0] !== enabled || $[1] !== idleTimeoutMs || $[2] !== immediate || $[3] !== intervalMs || $[4] !== onLostAttentionRef || $[5] !== onTickRef) {
    t0 = () => {
      if (!enabled || !Number.isFinite(intervalMs) || intervalMs <= 0) {
        return;
      }
      const hasFocusFn = typeof document.hasFocus === "function" ? _temp : _temp2;
      let hasFocus = hasFocusFn();
      let isVisible = document.visibilityState === "visible";
      let lastActivity = performance.now();
      let lastActivityUpdate = 0;
      let isActive = true;
      let wasAttentive = hasFocus && isVisible && isActive;
      const isAttentive = function isAttentive() {
        return hasFocus && isVisible && isActive;
      };
      const checkTransition = function checkTransition() {
        const attentive = isAttentive();
        if (wasAttentive && !attentive) {
          onLostAttentionRef.current?.();
        }
        if (!wasAttentive && attentive && immediate) {
          onTickRef.current();
        }
        wasAttentive = attentive;
      };
      if (immediate && wasAttentive) {
        onTickRef.current();
      }
      const handleFocus = function handleFocus() {
        hasFocus = true;
        checkTransition();
      };
      const handleBlur = function handleBlur() {
        hasFocus = hasFocusFn();
        checkTransition();
      };
      const handleVisibilityChange = function handleVisibilityChange() {
        isVisible = document.visibilityState === "visible";
        checkTransition();
      };
      const handleActivity = function handleActivity() {
        const now = performance.now();
        if (now - lastActivityUpdate < ACTIVITY_THROTTLE_MS) {
          return;
        }
        lastActivityUpdate = now;
        lastActivity = now;
        const wasActive = isActive;
        isActive = true;
        if (!wasActive) {
          checkTransition();
        }
      };
      window.addEventListener("focus", handleFocus);
      window.addEventListener("blur", handleBlur);
      document.addEventListener("visibilitychange", handleVisibilityChange);
      document.addEventListener("mousemove", handleActivity);
      document.addEventListener("mousedown", handleActivity);
      document.addEventListener("pointerdown", handleActivity);
      document.addEventListener("keydown", handleActivity);
      document.addEventListener("touchstart", handleActivity);
      const mainInterval = globalThis.setInterval(() => {
        if (isAttentive()) {
          onTickRef.current();
        }
      }, intervalMs);
      const idleCheckInterval = globalThis.setInterval(() => {
        const elapsed = performance.now() - lastActivity;
        if (isActive && elapsed > idleTimeoutMs) {
          isActive = false;
          checkTransition();
        }
      }, IDLE_CHECK_INTERVAL_MS);
      return () => {
        window.removeEventListener("focus", handleFocus);
        window.removeEventListener("blur", handleBlur);
        document.removeEventListener("visibilitychange", handleVisibilityChange);
        document.removeEventListener("mousemove", handleActivity);
        document.removeEventListener("mousedown", handleActivity);
        document.removeEventListener("pointerdown", handleActivity);
        document.removeEventListener("keydown", handleActivity);
        document.removeEventListener("touchstart", handleActivity);
        globalThis.clearInterval(mainInterval);
        globalThis.clearInterval(idleCheckInterval);
      };
    };
    $[0] = enabled;
    $[1] = idleTimeoutMs;
    $[2] = immediate;
    $[3] = intervalMs;
    $[4] = onLostAttentionRef;
    $[5] = onTickRef;
    $[6] = t0;
  } else {
    t0 = $[6];
  }
  let t1;
  if ($[7] !== enabled || $[8] !== idleTimeoutMs || $[9] !== immediate || $[10] !== intervalMs) {
    t1 = [enabled, intervalMs, immediate, idleTimeoutMs];
    $[7] = enabled;
    $[8] = idleTimeoutMs;
    $[9] = immediate;
    $[10] = intervalMs;
    $[11] = t1;
  } else {
    t1 = $[11];
  }
  useEffect(t0, t1);
}
function _temp2() {
  return true;
}
function _temp() {
  return document.hasFocus();
}